import { useRouter } from 'next/router'
import { useCallback, type FunctionComponent } from 'react'
import type { FilterQuery } from '../../const/FilterQuery'
import { useTranslate } from '../../contexts/TranslationContext'
import SvgCross from '../../svg/icons/Cross'
import { Button } from '../shared/Button'
import { Checkbox } from './Checkbox'
import style from './CheckboxGroup.module.sass'

type CheckboxGroupProps = {
	items: { id: string; name: string }[] | string[]
	isDeleteAllVisible?: boolean
	defaultChecked?: string[]
	label: string
	initialQuery: FilterQuery
}

export const CheckboxGroup: FunctionComponent<CheckboxGroupProps> = ({
	items,
	defaultChecked,
	label,
	initialQuery,
}) => {
	const router = useRouter()
	const { query } = useRouter()

	const translate = useTranslate()

	const handleChange = useCallback(
		(key: string, id: string | number) => {
			const itemQueryArray = initialQuery[key as keyof FilterQuery] ?? []

			const stringId = String(id)
			const isChecked = itemQueryArray.includes(stringId)

			if (!isChecked) {
				itemQueryArray.push(stringId)
			} else {
				const index = itemQueryArray.indexOf(stringId)
				if (index !== -1) {
					itemQueryArray.splice(index, 1)
				}
			}

			router.replace({ pathname: router.pathname, query: { ...query, [key]: itemQueryArray } }, undefined, {
				scroll: false,
			})
		},
		[initialQuery, query, router],
	)

	const deleteQuery = (label: string) => {
		router.replace({ pathname: router.pathname, query: { ...query, [label]: [] } }, undefined, {
			scroll: false,
		})
	}

	return (
		<div className={style.root}>
			{items.map((item) =>
				typeof item === 'string' ? (
					<Checkbox
						key={item}
						label={item}
						onChange={() => {
							handleChange(label, item)
						}}
						checked={
							defaultChecked
								? defaultChecked.includes(item)
								: ((initialQuery[label as keyof FilterQuery] &&
										(initialQuery[label as keyof FilterQuery] as string[]).includes(item)) ??
									false)
						}
					/>
				) : (
					<Checkbox
						key={item.id}
						label={item.name}
						onChange={() => {
							handleChange(label, item.name)
						}}
						checked={
							defaultChecked
								? defaultChecked.includes(item.id)
								: ((initialQuery[label as keyof FilterQuery] &&
										(initialQuery[label as keyof FilterQuery] as string[]).includes(item.name)) ??
									false)
						}
					/>
				),
			)}
			{Boolean(initialQuery[label as keyof FilterQuery]) && (
				<div className={style.buttonWrapper}>
					<Button
						label={translate('checkboxGroup.clear')}
						icon={{ small: true }}
						iconBefore={<SvgCross />}
						type={{ variant: 'underlinedGrey' }}
						onClick={() => deleteQuery(label as keyof FilterQuery)}
					/>
				</div>
			)}
		</div>
	)
}
